import { z } from 'zod';

/** Remove tudo que não for dígito. */
export function onlyDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

/** lowercase, sem acentos, trim e espaços colapsados. */
export function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Código de escola/matrícula: maiúsculo, sem espaços. */
export function normalizeCode(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .trim();
}

/** Valida CPF (formato e dígitos verificadores). */
export function isValidCpf(input: string): boolean {
  const cpf = onlyDigits(input);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const digits = cpf.split('').map(Number);
  const calc = (length: number): number => {
    let sum = 0;
    for (let i = 0; i < length; i += 1) sum += (digits[i] ?? 0) * (length + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return calc(9) === digits[9] && calc(10) === digits[10];
}

export function formatCpf(input: string): string {
  const d = onlyDigits(input).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

/**
 * Documento de aluno pode ser RG (formato livre por UF) ou CPF.
 * Se tiver 11 dígitos numéricos e formato de CPF, valida como CPF.
 */
export function looksLikeCpf(input: string): boolean {
  return /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(input.trim());
}

/** Mascara para exibição: 529.***.***-42 */
export function maskDocument(input: string | undefined | null): string | undefined {
  if (!input) return undefined;
  const d = onlyDigits(input);
  if (d.length === 11) return `${d.slice(0, 3)}.***.***-${d.slice(9)}`;
  if (input.length <= 4) return '****';
  return `${input.slice(0, 2)}${'*'.repeat(Math.max(input.length - 4, 2))}${input.slice(-2)}`;
}

export const CpfSchema = z
  .string()
  .trim()
  .refine(isValidCpf, { message: 'CPF inválido' })
  .transform((v) => formatCpf(v));

/** RG ou CPF. Se tiver formato de CPF, valida dígitos. */
export const PersonDocumentSchema = z
  .string()
  .trim()
  .min(5, 'Documento muito curto')
  .max(20, 'Documento muito longo')
  .regex(/^[0-9A-Za-z.\-/ ]+$/, 'Documento contém caracteres inválidos')
  .superRefine((value, ctx) => {
    if (looksLikeCpf(value) && !isValidCpf(value)) {
      ctx.addIssue({ code: 'custom', message: 'CPF inválido' });
    }
  })
  .transform((v) => (looksLikeCpf(v) ? formatCpf(v) : v.toUpperCase()));

/** Código INEP: aceita "35123456" ou "3512-3456"; normaliza para 8 dígitos. */
export const InepCodeSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^\d{8}$/, 'Código INEP deve conter exatamente 8 dígitos'));

export const EnrollmentNumberSchema = z
  .string()
  .trim()
  .min(3, 'Matrícula muito curta')
  .max(30, 'Matrícula muito longa')
  .regex(/^[A-Za-z0-9-_/]+$/, 'Use apenas letras, números e hífen')
  .transform((v) => v.toUpperCase());

export const PersonNameSchema = z
  .string()
  .trim()
  .min(3, 'Informe o nome completo')
  .max(120, 'Nome muito longo')
  .transform((v) => v.replace(/\s+/g, ' '));

export const IdSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/, 'Identificador inválido');

export const IsoDateTimeSchema = z.iso.datetime({ offset: true });
export const IsoDateSchema = z.iso.date();

/**
 * Gera tokens de busca por prefixo (sem dados sensíveis).
 * Inclui prefixos de cada palavra e do texto completo.
 */
export function buildSearchTokens(values: Array<string | undefined | null>, maxPrefix = 20): string[] {
  const tokens = new Set<string>();
  for (const raw of values) {
    if (!raw) continue;
    const normalized = normalizeText(raw);
    if (!normalized) continue;
    const addPrefixes = (text: string) => {
      const limit = Math.min(text.length, maxPrefix);
      for (let i = 2; i <= limit; i += 1) tokens.add(text.slice(0, i));
    };
    addPrefixes(normalized);
    for (const word of normalized.split(' ')) addPrefixes(word);
  }
  return [...tokens];
}

/** Converte a busca digitada no token que será procurado em searchTokens. */
export function searchQueryToken(query: string, maxPrefix = 20): string | null {
  const normalized = normalizeText(query);
  if (normalized.length < 2) return null;
  return normalized.slice(0, maxPrefix).trim();
}

/**
 * Código de acesso da escola = código INEP (8 dígitos) no formato XXXX-XXXX.
 * Retorna null quando a entrada não tem exatamente 8 dígitos.
 */
export function formatSchoolCode(input: string): string | null {
  const digits = onlyDigits(input);
  return digits.length === 8 ? `${digits.slice(0, 4)}-${digits.slice(4)}` : null;
}

/** Normaliza o que o usuário digitou no login ("35123456", "3512 3456", "3512-3456"). */
export function normalizeSchoolCode(input: string): string {
  return formatSchoolCode(input) ?? normalizeCode(input);
}

export const SchoolCodeSchema = z
  .string()
  .trim()
  .refine((v) => formatSchoolCode(v) !== null && /^[\d\s-]+$/.test(v), {
    message: 'Informe o código INEP da escola (8 dígitos: XXXX-XXXX)',
  })
  .transform((v) => formatSchoolCode(v)!);

/** Domínio de e-mail da equipe TaskAll (acesso de plataforma). */
export const PLATFORM_EMAIL_DOMAIN = 'taskall.ai';

export function isPlatformEmail(email: string | null | undefined, domain = PLATFORM_EMAIL_DOMAIN): boolean {
  if (!email) return false;
  return email.trim().toLowerCase().endsWith(`@${domain.toLowerCase()}`);
}
